import { InfoListItem } from '@brightlayer-ui/react-components';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { useExportTrendsMutation } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useConfirm } from '@fiji/common/src/hooks';
import { getTimeInFromTo } from '@fiji/common/src/utils/helpers';
import { Download, Send } from '@mui/icons-material';
import {
    Stack,
    Typography,
    FormControl,
    Select,
    MenuItem,
    List,
    Checkbox,
    Button,
    CircularProgress,
} from '@mui/material';
import { CustomModal, CustomTransComponent } from 'components';
import { useNewCustomTranslations } from 'hooks';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { AutoCompleteInput } from 'pages/AdvanceExportTrends/AutoCompleteInput';
import { addRemoveArrayHandler } from 'pages/AdvanceExportTrends/ExportMethod';
import React, { useId, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { downloadFileFromLink, getUTCTimestamp } from 'utils/helpers';

const initialExportPayload: any = { emails: [], fileType: 'CSV', types: [] };

export const ExportModal = React.forwardRef((props: any, ref) => {
    const modalRef = React.useRef<any>(null);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [snackId, setSnackId] = React.useState(0);
    const id = useId();
    useNewCustomTranslations('TRENDS');
    const [inputValue, setInputValue] = React.useState('');
    const [exportPayload, setExportPayload] = React.useState(initialExportPayload);

    const [exportTrends, { data: exportedData, isLoading: isExporting, isSuccess, isError }] =
        useExportTrendsMutation();
    const submitHandler = async (payload: any): Promise<void> => {
        const clone = JSON.parse(
            JSON.stringify({
                ...exportPayload,
                isPDFExport: exportPayload?.fileType === 'PDF',
                [exportPayload?.fileType === 'PDF' ? 'pdfExport' : 'exportRequests']:
                    exportPayload?.fileType === 'PDF'
                        ? payload?.payload
                        : payload?.payload?.map((item: any) => ({
                              ...item,
                              channelId: item?.channelId?.map((channel: any) => channel?.channelId),
                          })),
                chartType: payload?.chartType,
                emails: exportPayload?.emails?.map((item: any) => item?.email),
            })
        );

        if (props?.loadTrend) {
            delete clone?.isPDFExport;
            clone['loadTrend'] = true;
            clone['exportRequests'] = payload?.payload;
        }

        if (payload?.duration) {
            const newDate: any = getTimeInFromTo(payload?.duration ?? 0, 'h');
            clone['from'] = getUTCTimestamp(newDate?.from);
            clone['to'] = getUTCTimestamp(newDate?.to);
        } else if (payload?.from && !props?.activeDuration) {
            clone['from'] = getUTCTimestamp(payload?.from);
            clone['to'] = getUTCTimestamp(payload?.to);
        } else if (props?.activeDuration) {
            const time = getTimeInFromTo(props?.activeDuration, 'h');
            clone['from'] = getUTCTimestamp(time?.from);
            clone['to'] = getUTCTimestamp(time?.to);
        }
        if (!exportPayload?.types?.includes?.('EMAIL')) {
            delete clone?.emails;
        }
        delete clone.fileType;
        const { error }: any = await exportTrends({
            body: clone,
            params: { type: exportPayload?.fileType },
        });
        if (!error) {
            setExportPayload(initialExportPayload);
            onCancel();
        }
    };

    const { isVisible, onCancel, onClick, onConfirm } = useConfirm(submitHandler as any);

    React.useImperativeHandle(ref, () => ({
        onClick: (action: boolean): void => onClick(action),
    }));

    const handleInputChange = (e: any, value: any): void => {
        setExportPayload((prev: any) => ({
            ...prev,
            emails: [...(prev?.emails ?? []), value],
        }));
        setInputValue('');
    };

    const handleSearchKey = (e: any): any => {
        if (e?.target) setInputValue(e?.target?.value ?? '');
    };
    const action = (snackbarKey: any): JSX.Element => (
        <Button
            variant="text"
            sx={{ color: '#80BDE0' }}
            name={snackbarKey}
            onClick={() => {
                closeSnackbar(+snackbarKey);
            }}
        >
            <CustomTransComponent translationKey={'COMMON:OK'} />
        </Button>
    );

    const actionDownload = (snackbarKey: any): JSX.Element => (
        <Button
            variant="text"
            sx={{ color: '#80BDE0' }}
            name={snackbarKey}
            onClick={() => {
                closeSnackbar(+snackbarKey);
                if (exportedData?.data) downloadFileFromLink(exportedData?.data, 'trends');
                else
                    dispatch(
                        setMessageContent({
                            isOpen: true,
                            message: t('COMMON:NO_DATA'),
                            duration: 3000,
                        })
                    );
            }}
        >
            <CustomTransComponent translationKey={'COMMON:OK'} />
        </Button>
    );

    React.useEffect(() => {
        const snackbarProps: any = {
            variant: 'notificationWithIcon',
            subtitle: t('COMMON:PLEASE_DO_NOT_CLOSE_THE_WINDOW'),
            Icon: <CircularProgress size={20} />,
            autoHideDuration: null,
            notificationWithAction: false,
            setId: setSnackId,
        };
        if (isExporting) {
            enqueueSnackbar(t('TRENDS:ADVANCED_EXPORT.ADVANCE_EXPORT_EXPORTING'), snackbarProps);
        }
    }, [isExporting]);

    React.useEffect(() => {
        const snackbarProps: any = {
            variant: 'notificationWithIcon',
            subtitle: '',
            autoHideDuration: 60000,
            Icon: <Download />,
            notificationWithAction: true,
            notificationAction: actionDownload,
        };
        const snackbarPropsEmail: any = {
            variant: 'notificationWithIcon',
            subtitle: <CustomTransComponent translationKey={'AUDIT_LOGS:EMAIL_SUCCESS'} />,
            autoHideDuration: 3000,
            Icon: <Send />,
            notificationWithAction: true,
            notificationAction: action,
        };

        if (isSuccess || isError) {
            closeSnackbar(+snackId);
        }
        if (isSuccess) {
            if (exportPayload?.types?.includes('EMAIL')) {
                enqueueSnackbar(t('COMMON:SENT'), snackbarPropsEmail);
            }
            if (exportPayload?.types?.includes('DOWNLOAD')) {
                enqueueSnackbar(t('TRENDS:ADVANCED_EXPORT.ADVANCE_EXPORT_READY_TO_DOWNLOAD'), snackbarProps);
            }
        }
    }, [isError, isSuccess]);

    const getIsDisabled = (): boolean => {
        if (exportPayload?.fileType) {
            if (exportPayload?.types?.includes?.('DOWNLOAD') && !exportPayload?.types?.includes?.('EMAIL')) {
                return false;
            } else if (exportPayload?.types?.includes?.('EMAIL')) {
                return !exportPayload?.emails?.length;
            }
            return true;
        }
        return true;
    };

    const handleTagDelete = (emailId: number): void => {
        const exportPayloadClone = JSON.parse(JSON.stringify(exportPayload));
        const selectedIndex = exportPayload?.emails?.findIndex((email: any) => email?.email === emailId);
        if (selectedIndex !== -1) {
            exportPayloadClone?.emails?.splice(selectedIndex, 1);
        }
        setExportPayload(exportPayloadClone);
    };

    const onPressEnter = (e: any, user: any): void => {
        const exportPayloadClone = JSON.parse(JSON.stringify(exportPayload));
        const selectedIndex = exportPayload?.emails?.findIndex((email: any) => email?.email === e.target.value);
        if (selectedIndex !== -1) {
            exportPayloadClone?.emails?.splice(selectedIndex, 1);
        } else {
            exportPayloadClone?.emails?.push(user ? user : { id: `${id}-${e.target.value}`, email: e.target.value });
        }
        setExportPayload(exportPayloadClone);
        setInputValue('');
    };

    const ExportTrendActions = [
        {
            key: 'cancel',
            label: t('COMMON:CANCEL'),
            // close: true,
            variant: 'outlined',
            handleClick: (): void => {
                setExportPayload(initialExportPayload);
                onCancel();
            },
        },
        ...(props?.advanceAction
            ? [
                  {
                      key: 'ax',
                      label: <CustomTransComponent translationKey={'COMMON:EXPORT_MODAL_ADVANCED'} />,
                      stackedKey: 'stacked',
                      variant: 'outlined',
                      handleClick: (): void => props?.advanceAction(exportPayload?.fileType),
                  },
              ]
            : []),
        {
            key: 'export',
            label: <CustomTransComponent translationKey={'COMMON:EXPORT'} />,
            ...(props?.advanceAction && { stackedKey: 'stacked' }),
            disabled: getIsDisabled() || isExporting,
            variant: 'contained',
            handleClick: onConfirm,
        },
    ];

    // const handleKeyDown = (e: any): void => {
    //     if (e.code === 'Enter' && exportPayload?.emails?.length < 6) {
    //         const tagValue = e.target.value;
    //         setExportPayload((prev: any) => ({
    //             ...prev,
    //             emails: [...(prev?.emails ?? []), tagValue],
    //         }));
    //         setInputValue('');
    //     }
    // };
    const useSplitFooter = Boolean(props?.splitFooterActions);
    const useStackedFooter = props?.advanceAction ? true : !useSplitFooter;

    return (
        <CustomModal
            ref={modalRef}
            isOpen={isVisible}
            isLoading={isExporting}
            type="primary"
            isStacked={useStackedFooter}
            actions={ExportTrendActions as any}
            noDividers
            actionsDivider
            header={props?.header}
            footerClass={useSplitFooter || props?.advanceAction ? 'space-between-footer' : ''}
        >
            <>
                <Stack spacing={2} my={2}>
                    {!props?.loadTrend ? (
                        <Typography variant="body1" mb={2}>
                            <CustomTransComponent translationKey={'AUDIT_LOGS:FILE_FORMAT'} />
                        </Typography>
                    ) : (
                        <Typography variant="body1" mb={2}>
                            Choose file type
                        </Typography>
                    )}
                    <FormControl fullWidth>
                        <Select
                            className="custom-select"
                            labelId="demo-simple-select-helper-label"
                            id="chooseFileType"
                            label={t('COMMON:AUDIT_LOGS_TIME')}
                            variant="filled"
                            fullWidth
                            disableUnderline
                            value={exportPayload?.fileType}
                            onChange={(e: any): void =>
                                setExportPayload((prev: any) => ({ ...prev, fileType: e?.target?.value }))
                            }
                        >
                            <MenuItem value={'CSV'} id="csv">
                                <CustomTransComponent translationKey={'AUDIT_LOGS:FILE_OPTIONS.CSV'} />
                            </MenuItem>
                            <MenuItem value={'EXCEL'} id="excel">
                                <CustomTransComponent translationKey={'AUDIT_LOGS:FILE_OPTIONS.EXCEL'} />
                            </MenuItem>
                            {!props?.loadTrend && (
                                <MenuItem value={'PDF'} id="pdf">
                                    <CustomTransComponent translationKey={'AUDIT_LOGS:FILE_OPTIONS.PDF'} />
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Stack>
                <Stack spacing={2} mt={3}>
                    <Typography variant="body1" mb={2}>
                        <CustomTransComponent translationKey={'AUDIT_LOGS:FILE_LOCATION'} />
                    </Typography>
                    <List disablePadding>
                        <InfoListItem
                            disablePadding
                            title={
                                <Typography variant="body1">
                                    <CustomTransComponent translationKey={'COMMON:DOWNLOAD'} />
                                </Typography>
                            }
                            icon={
                                <Checkbox
                                    id="download"
                                    checked={exportPayload?.types?.includes('DOWNLOAD')}
                                    onChange={(): void =>
                                        setExportPayload((prev: any) => ({
                                            ...prev,
                                            types: addRemoveArrayHandler('DOWNLOAD', exportPayload?.types),
                                        }))
                                    }
                                />
                            }
                        />
                        <InfoListItem
                            disablePadding
                            title={
                                <Typography variant="body1">
                                    <CustomTransComponent translationKey={'COMMON:SEND_BY_MAIL'} />
                                </Typography>
                            }
                            icon={
                                <Checkbox
                                    id="email"
                                    checked={exportPayload?.types?.includes('EMAIL')}
                                    onChange={(): void =>
                                        setExportPayload((prev: any) => ({
                                            ...prev,
                                            types: addRemoveArrayHandler('EMAIL', exportPayload?.types),
                                        }))
                                    }
                                />
                            }
                        />
                    </List>
                </Stack>
                {exportPayload?.types?.includes?.('EMAIL') && (
                    <AutoCompleteInput
                        isTextField
                        value={exportPayload?.emails ?? []}
                        handleChipDelete={handleTagDelete}
                        onKeyDown={(e: any, user: any) => {
                            if (e?.code === 'Enter' && e.target.value) {
                                onPressEnter(e, user);
                            }
                        }}
                        onChange={handleInputChange}
                        inputValue={inputValue}
                        isDisabled={false}
                        onInputChange={handleSearchKey}
                    />
                )}
            </>
        </CustomModal>
    );
});
